import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { Activity, TrendingUp, Users, PlayCircle, Shield, UserCheck, Briefcase, Database, AlertCircle, Clock } from 'lucide-react';
import { backendClient } from '@/lib/backendClient';
import { Badge } from '@/components/ui/badge';
import { useStorageMetrics } from '@/hooks/useMonitoringMetrics';

interface EnhancedAnalyticsData {
  // Basic metrics
  totalSessions: number;
  totalUsers: number;
  activeUsers: number;
  storageUsage: number;
  
  // Role-based analytics
  totalAdmins: number;
  totalModerators: number;
  totalPractitioners: number;
  totalRegularUsers: number;
  
  // Business intelligence
  practitionerMetrics: {
    totalClients: number;
    averageClientsPerPractitioner: number;
    mostActivePartitioners: Array<{ 
      name: string; 
      clients: number; 
      sessions: number;
      hasCompleteProfile?: boolean;
      hasProfile?: boolean;
    }>;
  };
  
  // Growth metrics
  userGrowth: Array<{ date: string; admins: number; practitioners: number; users: number; total: number }>;
  sessionGrowth: Array<{ date: string; sessions: number; users: number }>;
  
  // Category performance
  categoryData: Array<{ name: string; value: number; color: string; growth: number }>;
  
  // Engagement metrics
  engagementData: {
    averageSessionsPerUser: number;
    sessionCompletionRate: number;
    userRetentionRate: number;
  };
  
  // Data integrity
  dataIntegrityIssues: string[];
}

const EnhancedSystemAnalytics: React.FC = () => {
  const [data, setData] = useState<EnhancedAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  // Use real storage metrics hook
  const { data: storageData } = useStorageMetrics();

  useEffect(() => {
    fetchEnhancedAnalytics();
  }, [storageData]); // Re-fetch when storage data changes

  const fetchEnhancedAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [
        sessionsResult,
        usersResult,
        rolesResult,
        categoriesResult,
        clientsResult
      ] = await Promise.all([
        backendClient.from('sessions').select('*'),
        backendClient.from('profiles').select('*'),
        backendClient.from('user_roles').select('*'),
        backendClient.from('categories').select('*'),
        backendClient.from('practitioner_clients').select('*')
      ]);

      const sessions = sessionsResult.data || [];
      const users = usersResult.data || [];
      const roles = rolesResult.data || [];
      const categories = categoriesResult.data || [];
      const clients = clientsResult.data || [];

      // Calculate role distribution
      const roleCount = {
        admin: roles.filter(r => r.role === 'admin').length,
        moderator: roles.filter(r => r.role === 'moderator').length,
        practitioner: roles.filter(r => r.role === 'practitioner').length,
        user: users.length - roles.length + roles.filter(r => r.role === 'user').length
      };

      // Calculate practitioner metrics - include ALL practitioners with roles
      const practitionerIds = roles.filter(r => r.role === 'practitioner').map(r => r.user_id);
      const practitionerClients = practitionerIds.map((pid, index) => {
        const practitionerSessions = sessions.filter(s => s.practitioner_id === pid);
        const clientCount = clients.filter(c => c.practitioner_id === pid && c.is_active).length;
        const practitioner = users.find(u => u.id === pid);
        
        // Show warning for practitioners without complete profiles
        const hasCompleteProfile = !!(practitioner?.full_name || practitioner?.first_name);
        const displayName = practitioner?.full_name || 
                          practitioner?.first_name || 
                          `Practitioner ${index + 1}`;
        
        return {
          name: displayName,
          clients: clientCount,
          sessions: practitionerSessions.length,
          hasCompleteProfile,
          hasProfile: !!practitioner
        };
      }).sort((a, b) => b.sessions - a.sessions).slice(0, 5);

      // Data integrity checks
      const dataIntegrityIssues = [];
      
      // Check for practitioners without profiles
      const practitionersWithoutProfiles = practitionerIds.filter(pid => !users.find(u => u.id === pid));
      if (practitionersWithoutProfiles.length > 0) {
        dataIntegrityIssues.push(`${practitionersWithoutProfiles.length} practitioner(s) missing profile data`);
      }
      
      // Check for clients assigned to non-practitioners
      const orphanedClients = clients.filter(c => 
        c.is_active && !practitionerIds.includes(c.practitioner_id)
      );
      if (orphanedClients.length > 0) {
        dataIntegrityIssues.push(`${orphanedClients.length} client(s) assigned to invalid practitioners`);
      }

      // Calculate growth data for last 30 days
      const last30Days = Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();

      const userGrowth = last30Days.map(date => {
        const dayUsers = users.filter(u => u.created_at.startsWith(date));

        // Count users created on this day, grouped by their role
        let adminCount = 0;
        let practitionerCount = 0;
        let regularUserCount = 0;

        dayUsers.forEach(user => {
          // Find all roles for this user
          const userRoles = roles.filter(r => r.user_id === user.id);

          // Check if user has admin or practitioner role
          const isAdmin = userRoles.some(r => r.role === 'admin');
          const isPractitioner = userRoles.some(r => r.role === 'practitioner');

          if (isAdmin) {
            adminCount++;
          } else if (isPractitioner) {
            practitionerCount++;
          } else {
            regularUserCount++;
          }
        });

        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          admins: adminCount,
          practitioners: practitionerCount,
          users: regularUserCount,
          total: dayUsers.length
        };
      });

      const sessionGrowth = last30Days.map(date => {
        const daySessions = sessions.filter(s => s.created_at.startsWith(date));
        const dayActiveUsers = new Set(daySessions.map(s => s.user_id)).size;
        
        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          sessions: daySessions.length,
          users: dayActiveUsers
        };
      });

      // Enhanced category analysis
      const categoryData = categories.map(category => {
        const categorySessions = sessions.filter(s => s.category_id === category.id);
        const last7DaysSessions = categorySessions.filter(s => {
          const sessionDate = new Date(s.created_at);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return sessionDate > weekAgo;
        });
        
        const prev7DaysSessions = categorySessions.filter(s => {
          const sessionDate = new Date(s.created_at);
          const twoWeeksAgo = new Date();
          twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return sessionDate > twoWeeksAgo && sessionDate <= weekAgo;
        });

        const growth = prev7DaysSessions.length > 0 
          ? ((last7DaysSessions.length - prev7DaysSessions.length) / prev7DaysSessions.length) * 100
          : last7DaysSessions.length > 0 ? 100 : 0;

        return {
          name: category.name,
          value: categorySessions.length,
          color: category.color || '#8884d8',
          growth: Math.round(growth)
        };
      });

      // Calculate active users (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const activeUserIds = new Set(
        sessions
          .filter(s => new Date(s.created_at) > thirtyDaysAgo)
          .map(s => s.user_id)
      );

      // Engagement metrics
      const totalSessionUsers = new Set(sessions.map(s => s.user_id)).size;
      const averageSessionsPerUser = totalSessionUsers > 0 ? sessions.length / totalSessionUsers : 0;

      // Calculate REAL retention rate (users active in last 30 days vs total users created before that)
      const activeUserSet = new Set(
        sessions
          .filter(s => new Date(s.created_at) > thirtyDaysAgo)
          .map(s => s.user_id)
      );
      const usersCreatedBeforeThirtyDays = users.filter(u => new Date(u.created_at) < thirtyDaysAgo).length;
      const userRetentionRate = usersCreatedBeforeThirtyDays > 0
        ? Math.round((activeUserSet.size / usersCreatedBeforeThirtyDays) * 100)
        : 0;

      // Calculate REAL session completion rate (sessions with completed status)
      const completedSessions = sessions.filter(s =>
        s.status === 'completed' || s.status === 'success' || s.status === 'done'
      ).length;
      const sessionCompletionRate = sessions.length > 0
        ? Math.round((completedSessions / sessions.length) * 100)
        : 0;

      setData({
        totalSessions: sessions.length,
        totalUsers: users.length,
        activeUsers: activeUserIds.size,
        storageUsage: storageData?.total_size_mb || 0,
        
        totalAdmins: roleCount.admin,
        totalModerators: roleCount.moderator,
        totalPractitioners: roleCount.practitioner,
        totalRegularUsers: roleCount.user,
        
        practitionerMetrics: {
          totalClients: clients.filter(c => c.is_active).length,
          averageClientsPerPractitioner: practitionerIds.length > 0 
            ? Math.round(clients.filter(c => c.is_active).length / practitionerIds.length) 
            : 0,
          mostActivePartitioners: practitionerClients
        },
        
        userGrowth,
        sessionGrowth,
        categoryData,
        
        engagementData: {
          averageSessionsPerUser: Math.round(averageSessionsPerUser * 10) / 10,
          sessionCompletionRate,
          userRetentionRate
        },

        dataIntegrityIssues
      });

    } catch (error) {
      console.error('Error fetching enhanced analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

  return (
    <div className="space-y-6">
      {/* Enhanced Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Sessions</p>
                <p className="text-3xl font-bold text-blue-900">{data.totalSessions}</p>
                <p className="text-xs text-blue-600 mt-1">Across all users</p>
              </div>
              <PlayCircle className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Active Users</p>
                <p className="text-3xl font-bold text-green-900">{data.activeUsers}</p>
                <p className="text-xs text-green-600 mt-1">Last 30 days</p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Total Practitioners</p>
                <p className="text-3xl font-bold text-purple-900">{data.totalPractitioners}</p>
                <p className="text-xs text-purple-600 mt-1">{data.practitionerMetrics.totalClients} clients</p>
              </div>
              <Briefcase className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Avg Sessions/User</p>
                <p className="text-3xl font-bold text-orange-900">{data.engagementData.averageSessionsPerUser}</p>
                <p className="text-xs text-orange-600 mt-1">{data.engagementData.userRetentionRate}% retention</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold">{data.totalAdmins}</p>
              </div>
              <Shield className="h-6 w-6 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Moderators</p>
                <p className="text-2xl font-bold">{data.totalModerators}</p>
              </div>
              <UserCheck className="h-6 w-6 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Practitioners</p>
                <p className="text-2xl font-bold">{data.totalPractitioners}</p>
              </div>
              <Briefcase className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Regular Users</p>
                <p className="text-2xl font-bold">{data.totalRegularUsers}</p>
              </div>
              <Users className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Growth Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              User Growth (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="practitioners" stackId="1" stroke="#8884d8" fill="#8884d8" />
                <Area type="monotone" dataKey="users" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
                <Area type="monotone" dataKey="admins" stackId="1" stroke="#ffc658" fill="#ffc658" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Session Activity (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.sessionGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="sessions" stroke="#8884d8" strokeWidth={2} />
                <Line type="monotone" dataKey="users" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Category Performance & Top Practitioners */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Category Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.categoryData.map((category, index) => (
                <div key={`category-${index}-${category.name}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: category.color }}
                    />
                    <div>
                      <p className="font-medium">{category.name}</p>
                      <p className="text-sm text-muted-foreground">{category.value} sessions</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={category.growth >= 0 ? "default" : "destructive"}>
                      {category.growth >= 0 ? '+' : ''}{category.growth}%
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">vs last week</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Top Practitioners
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.practitionerMetrics.mostActivePartitioners.map((practitioner, index) => (
                <div key={`practitioner-${index}-${practitioner.name}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-medium">
                      #{index + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{practitioner.name}</p>
                        {!practitioner.hasCompleteProfile && (
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                        )}
                        {!practitioner.hasProfile && (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{practitioner.clients} clients</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{practitioner.sessions}</p>
                    <p className="text-xs text-muted-foreground">sessions</p>
                  </div>
                </div>
              ))}
              {data.practitionerMetrics.mostActivePartitioners.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No practitioners found</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Intelligence Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Business Intelligence Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-lg bg-blue-50">
              <Clock className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-900">{data.engagementData.sessionCompletionRate}%</p>
              <p className="text-sm text-blue-600">Session Completion Rate</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-green-50">
              <Users className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-900">{data.engagementData.userRetentionRate}%</p>
              <p className="text-sm text-green-600">User Retention (30 days)</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-purple-50">
              <Briefcase className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-900">{data.practitionerMetrics.averageClientsPerPractitioner}</p>
              <p className="text-sm text-purple-600">Avg Clients per Practitioner</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Data Integrity Issues */}
      {data.dataIntegrityIssues.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="h-5 w-5" />
              Data Integrity Issues Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.dataIntegrityIssues.map((issue, index) => (
                <div key={index} className="flex items-center gap-2 p-2 rounded bg-yellow-100">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <p className="text-sm text-yellow-800">{issue}</p>
                </div>
              ))}
              <p className="text-xs text-yellow-700 mt-3">
                These issues may affect data accuracy. Please review the practitioner and profile data.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnhancedSystemAnalytics;